import React, { JSX, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { DetailedHTMLProps, InputHTMLAttributes, useRef } from "react";
import { Transcriber } from "@/hooks/useTranscriber";
import Constants from "@/utils/AudioUtils";
import { webmFixDuration, formatAudioTimestamp } from "@/utils/AudioUtils";
import { ArrowRight, Loader2, RefreshCw, PlayCircle, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { registerStream, unRegisterStream } from "@/lib/MediaRegistry";
import { end } from "slate";


interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isModelLoading: boolean;
    isTranscribing: boolean;
    // isDisableButton: boolean;
    isCreatingAiJob: boolean;
    isTranscribed: boolean;
}

function TranscribeButton(props: Props) {
    const { isModelLoading, isTranscribing, isTranscribed, isCreatingAiJob, onClick, ...buttonProps } = props;
    const isLoading = isTranscribing || isModelLoading || isCreatingAiJob;
    return (
        <button
            {...buttonProps}
            onClick={(event) => {
                if (isTranscribing || isModelLoading) return;
                if (onClick) {
                    onClick(event);
                }
            }}
            disabled={isLoading}
            className={`w-full sm:w-auto font-semibold px-8 py-3 rounded-xl shadow-md transition-all duration-300 transform flex items-center justify-center gap-2
            ${isTranscribing
                    ? "bg-primary text-primary-foreground cursor-wait"
                    : isTranscribed
                        ? "border border-gray-400 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
                        : "bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground hover:shadow-xl hover:scale-105"
                }
            disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
        >
            {isTranscribed ? (
                <>
                    <RefreshCw className="w-5 h-5" />
                    <span>Regenerate</span>
                </>
            ) : (
                <>
                    <Upload className="w-5 h-5" />
                    <span>Upload Audio</span>
                </>
            )}
        </button>

    );
}

export function Spinner(props: { text: string }): JSX.Element {
    return (
        <div role='status'>
            <svg
                aria-hidden='true'
                role='status'
                className='inline w-4 h-4 mr-3 text-white animate-spin'
                viewBox='0 0 100 101'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                    fill='#E5E7EB'
                />
                <path
                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                    fill='currentColor'
                />
            </svg>
            {props.text}
        </div>
    );
}


// function Progress({
//     text,
//     percentage,
// }: {
//     text: string;
//     percentage: number;
// }) {
//     percentage = percentage ?? 0;
//     return (
//         <div className='mt-0.5 w-full relative text-sm text-white background-bg-cyan-400 bg-gray-200 border-1 border-gray-400 rounded-lg text-left overflow-hidden'>
//             <div
//                 className='top-0 h-full bg-blue-500 whitespace-nowrap px-2'
//                 style={{ width: `${percentage}%` }}
//             >
//                 {text} ({`${percentage.toFixed(2)}%`})
//             </div>
//         </div>
//     );
// }

function getMimeType() {
    const types = [
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
        "audio/wav",
        "audio/aac",
    ];
    for (let i = 0; i < types.length; i++) {
        if (MediaRecorder.isTypeSupported(types[i])) {
            return types[i];
        }
    }
    return undefined;
}

function AudioRecorder(props: {
    onRecordingComplete: (blob: Blob) => void;
}) {
    useEffect(() => {
        return () => {
            unRegisterStream("AudioRecorder-audio-stream");
        };
    }, []);
    const [recording, setRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);


    const streamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const audioRef = useRef<HTMLAudioElement | null>(null);

    const startRecording = async () => {
        // Reset recording (if any)
        setRecordedBlob(null);

        const startTime = Date.now();

        try {
            if (!streamRef.current) {
                streamRef.current = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });
                unRegisterStream("AudioRecorder-audio-stream");
                registerStream("AudioRecorder-audio-stream", streamRef.current);
            }

            const mimeType = getMimeType();
            const mediaRecorder = new MediaRecorder(streamRef.current, {
                mimeType,
            });

            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.addEventListener("dataavailable", async (event) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
                if (mediaRecorder.state === "inactive") {
                    const duration = Date.now() - startTime;

                    // Received a stop event
                    let blob = new Blob(chunksRef.current, { type: mimeType });

                    if (mimeType === "audio/webm") {
                        blob = await webmFixDuration(blob, duration, blob.type);
                    }

                    setRecordedBlob(blob);
                    props.onRecordingComplete(blob);

                    chunksRef.current = [];
                }
            });
            mediaRecorder.start();
            setRecording(true);
        } catch (error) {
            console.error("Error accessing microphone:", error);
        }
    };

    const stopRecording = () => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
        ) {
            mediaRecorderRef.current.stop(); // set state to inactive
            setDuration(0);
            setRecording(false);
        }
    };

    useEffect(() => {
        // const stream: MediaStream | null = null;

        if (recording) {
            const timer = setInterval(() => {
                setDuration((prevDuration) => prevDuration + 1);
            }, 1000);

            return () => {
                clearInterval(timer);
            };
        }

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, [recording]);


    const handleToggleRecording = () => {
        if (recording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    return (
        <div className='flex flex-col justify-center items-center'>
            <button
                type='button'
                className={`m-2 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 transition-all duration-200 ${recording
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-green-500 hover:bg-green-600"
                    }`}
                onClick={handleToggleRecording}
            >
                {recording
                    ? `Stop Recording (${formatAudioTimestamp(duration)})`
                    : "Start Recording"}
            </button>

            {recordedBlob && (
                <audio className='w-full' ref={audioRef} controls>
                    <source
                        src={URL.createObjectURL(recordedBlob)}
                        type={recordedBlob.type}
                    />
                </audio>
            )}
        </div>
    );
}


function titleCase(str: string) {
    str = str.toLowerCase();
    return (str.match(/\w+.?/g) || [])
        .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join("");
}

// Modal component
interface ModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: () => void;
    submitText?: string;
    submitEnabled?: boolean;
    title: string | JSX.Element;
    content: string | JSX.Element;
}

function Modal({
    show,
    onClose,
    onSubmit,
    title,
    content,
    submitText,
    submitEnabled = true,
}: ModalProps) {
    return (
        <Dialog.Root open={show} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

                <Dialog.Content className="fixed top-1/2 left-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-6 shadow-2xl animate-scale-in">
                    <Dialog.Title className="text-lg font-semibold text-gray-100">
                        {title}
                    </Dialog.Title>

                    <div className="mt-3 text-sm text-gray-300">{content}</div>

                    <div className="mt-6 flex flex-row-reverse gap-3">
                        {submitText && (
                            <button
                                type="button"
                                disabled={!submitEnabled}
                                className={`inline-flex justify-center rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 shadow-md
                    ${submitEnabled
                                        ? "bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                        : "bg-gray-600 text-gray-300 cursor-not-allowed opacity-70"
                                    }`}
                                onClick={onSubmit}
                            >
                                {submitText}
                            </button>
                        )}

                        <Dialog.Close asChild>
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400 transition-all duration-300"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </Dialog.Close>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

// UrlInput component
function UrlInput(
    props: DetailedHTMLProps<
        InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    >,
) {
    return (
        <div>
            <input
                {...props}
                type='url'
                className='my-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                placeholder='www.example.com'
                required
            />
        </div>
    );
}


export default function AudioPlayer(props: {
    audioUrl: string;
    mimeType: string;
    isAIModulePage?: boolean;
    lastStartTimeRef?: React.MutableRefObject<number>;
    pauseTimeRef?: React.MutableRefObject<number>;
    endTimeRef?: React.MutableRefObject<number>;
    // handleSegmentedTranscription?: () => void;
    isProcessing?: boolean;
}) {
    const audioPlayer = useRef<HTMLAudioElement>(null);
    const audioSource = useRef<HTMLSourceElement>(null);

    useEffect(() => {
        if (audioPlayer.current && audioSource.current) {
            audioSource.current.src = props.audioUrl;
            audioPlayer.current.load();
        }
    }, [props.audioUrl]);

    if (!props.isAIModulePage) {
        return (
            <audio ref={audioPlayer} controls className="w-full h-14">
                <source ref={audioSource} type={props.mimeType} />
            </audio>
        );
    }

    return (
        <div
  className={`relative w-full ${
    props.isProcessing ? "pointer-events-none opacity-60" : ""
  }`}
>
        {/* <audio
            ref={audioPlayer}
            controls
            className="w-full h-14"
            onPlay={() => {
                if (!props.lastStartTimeRef || !audioPlayer.current) return;
                props.lastStartTimeRef.current = audioPlayer.current.currentTime;
            }}
            onPause={() => {
                if (!props.pauseTimeRef || !audioPlayer.current) return;
                props.pauseTimeRef.current = audioPlayer.current.currentTime;
                props.handleSegmentedTranscription?.();
            }}
            onSeeked={() => {
                if (!props.lastStartTimeRef || !audioPlayer.current) return;
                props.lastStartTimeRef.current = audioPlayer.current.currentTime;
            }}
            onEnded={() => {
                if (!props.endTimeRef || !audioPlayer.current) return;
                props.endTimeRef.current = audioPlayer.current.duration;
                props.handleSegmentedTranscription?.();
            }}
        >
            <source ref={audioSource} type={props.mimeType}/>
        </audio> */}
        </div>
    );
}

// List of supported languages:
// https://help.openai.com/en/articles/7031512-whisper-api-faq
// https://github.com/openai/whisper/blob/248b6cb124225dd263bb9bd32d060b6517e067f8/whisper/tokenizer.py#L79
const LANGUAGES = {
    en: "english",
    zh: "chinese",
    de: "german",
    es: "spanish/castilian",
    ru: "russian",
    ko: "korean",
    fr: "french",
    ja: "japanese",
    pt: "portuguese",
    tr: "turkish",
    pl: "polish",
    ca: "catalan/valencian",
    nl: "dutch/flemish",
    ar: "arabic",
    sv: "swedish",
    it: "italian",
    id: "indonesian",
    hi: "hindi",
    fi: "finnish",
    vi: "vietnamese",
    he: "hebrew",
    uk: "ukrainian",
    el: "greek",
    ms: "malay",
    cs: "czech",
    ro: "romanian/moldavian/moldovan",
    da: "danish",
    hu: "hungarian",
    ta: "tamil",
    no: "norwegian",
    th: "thai",
    ur: "urdu",
    hr: "croatian",
    bg: "bulgarian",
    lt: "lithuanian",
    la: "latin",
    mi: "maori",
    ml: "malayalam",
    cy: "welsh",
    sk: "slovak",
    te: "telugu",
    fa: "persian",
    lv: "latvian",
    bn: "bengali",
    sr: "serbian",
    az: "azerbaijani",
    sl: "slovenian",
    kn: "kannada",
    et: "estonian",
    mk: "macedonian",
    br: "breton",
    eu: "basque",
    is: "icelandic",
    hy: "armenian",
    ne: "nepali",
    mn: "mongolian",
    bs: "bosnian",
    kk: "kazakh",
    sq: "albanian",
    sw: "swahili",
    gl: "galician",
    mr: "marathi",
    pa: "punjabi/panjabi",
    si: "sinhala/sinhalese",
    km: "khmer",
    sn: "shona",
    yo: "yoruba",
    so: "somali",
    af: "afrikaans",
    oc: "occitan",
    ka: "georgian",
    be: "belarusian",
    tg: "tajik",
    sd: "sindhi",
    gu: "gujarati",
    am: "amharic",
    yi: "yiddish",
    lo: "lao",
    uz: "uzbek",
    fo: "faroese",
    ht: "haitian creole/haitian",
    ps: "pashto/pushto",
    tk: "turkmen",
    nn: "nynorsk",
    mt: "maltese",
    sa: "sanskrit",
    lb: "luxembourgish/letzeburgesch",
    my: "myanmar/burmese",
    bo: "tibetan",
    tl: "tagalog",
    mg: "malagasy",
    as: "assamese",
    tt: "tatar",
    haw: "hawaiian",
    ln: "lingala",
    ha: "hausa",
    ba: "bashkir",
    jw: "javanese",
    su: "sundanese",
};

export enum AudioSource {
    URL = "URL",
    FILE = "FILE",
    RECORDING = "RECORDING",
}




interface FileTileProps {
    onFileUpdate: (decoded: AudioBuffer, blobUrl: string, mimeType: string) => void;
    icon?: React.ReactNode;
    text?: string;
    isDisabled?: boolean;
}
const FileTile: React.FC<FileTileProps> = ({
    onFileUpdate,
    icon,
    text = "Upload File",
    isDisabled = false,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);


    const processFile = async (file: File) => {
        try {
            if (!file.type.startsWith("audio/")) {
                toast.error("Please select a valid audio file");
                return;
            }
            setLoading(true);
            const urlObj = URL.createObjectURL(file);
            const mimeType = file.type;

            const arrayBuffer = await file.arrayBuffer();
            const audioCTX = new AudioContext({
                sampleRate: Constants.SAMPLING_RATE,
            });
            const decoded = await audioCTX.decodeAudioData(arrayBuffer);

            onFileUpdate(decoded, urlObj, mimeType);
        } catch (err) {
            toast.error("Failed to process audio file");
        } finally {
            setLoading(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isDisabled || loading || loading) return;

        const audioFile = Array.from(e.dataTransfer.files).find((f) =>
            f.type.startsWith("audio/")
        );
        if (audioFile) processFile(audioFile);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
        if (e.target) e.target.value = ""; // reset so same file can be re-selected
    };

    const handleClick = () => {
        if (!isDisabled || loading && !loading) fileInputRef.current?.click();
    };

    return (
        <div
            className={`
        w-full h-36 border-2 border-dashed rounded-xl cursor-pointer
        flex flex-col items-center justify-center text-center px-8 py-24
        transition-transform duration-300 ease-out border-gray-400 dark:border-gray-600
        ${isDragOver
                    ? "border-primary bg-primary/5 scale-[1.02] shadow-lg shadow-primary/10"
                    : "border-border hover:border-primary/60 hover:scale-[1.02] hover:shadow-md"
                }
        ${isDisabled || loading ? "opacity-50 cursor-not-allowed hover:scale-100" : ""}
        `}
            onDrop={handleDrop}
            onDragOver={(e) => {
                e.preventDefault()
                if (!isDisabled || loading || !loading) setIsDragOver(true)
            }}
            onDragLeave={(e) => {
                e.preventDefault()
                setIsDragOver(false)
            }}
            onClick={handleClick}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isDisabled || loading}
            />

            {loading ? (
                <div className="flex flex-col items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-muted-foreground">Processing audio file...</p>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-4">
                    <div
                        className={`
              w-12 h-12 rounded-full flex items-center justify-center
              transition-all duration-300
              ${isDragOver ? "bg-primary/10 scale-110" : "bg-muted hover:bg-primary/5"}
            `}
                    >
                        {icon || (
                            <svg
                                className={`w-7 h-7 transition-colors duration-300 ${isDragOver
                                    ? "text-primary"
                                    : "text-muted-foreground hover:text-primary"
                                    }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                                />
                            </svg>
                        )}
                    </div>

                    <div className="space-y-2">
                        <p
                            className={`text-sm font-semibold transition-colors duration-300 ${isDragOver ? "text-primary" : "text-foreground"
                                }`}
                        >
                            {text}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {isDragOver
                                ? "Drop your audio file here"
                                : "Drag & drop or click to browse"}
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                            Supports MP3, WAV, M4A formats
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};




export function AudioManager(props: { transcriber: Transcriber, isEditingTranscription: boolean, jobError: string, createAiJob: () => void, isCreatingAiJob: boolean, isRunningAiJob: boolean, isAIModulePage?: boolean, isProcessing?: boolean, startTimeRef?: React.MutableRefObject<number | null>, pauseTimeRef?: React.MutableRefObject<number | null>, endTimeRef?: React.MutableRefObject<number | null>,  isPaused?: boolean, isBackgroundTranscribing?: React.MutableRefObject<boolean> },) {
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const [audioData, setAudioData] = useState<
        | {
            buffer: AudioBuffer;
            url: string;
            source: AudioSource;
            mimeType: string;
        }
        | undefined
    >(undefined);

    const [audioDownloadUrl, setAudioDownloadUrl] = useState<
        string | undefined
    >(undefined);

    const isAudioLoading = progress !== undefined;


    const lastProccessedTimeRef = useRef<number>(0);

    const [showFileInput, setShowFileInput] = useState(true);

    const resetAudio = () => {
        setAudioData(undefined);
        setAudioDownloadUrl(undefined);
    };

    const setAudioFromDownload = async (
        data: ArrayBuffer,
        mimeType: string,
    ) => {
        const audioCTX = new AudioContext({
            sampleRate: Constants.SAMPLING_RATE,
        });
        const blobUrl = URL.createObjectURL(
            new Blob([data], { type: "audio/*" }),
        );
        const decoded = await audioCTX.decodeAudioData(data);
        setAudioData({
            buffer: decoded,
            url: blobUrl,
            source: AudioSource.URL,
            mimeType: mimeType,
        });
    };

    const setAudioFromRecording = async (data: Blob) => {
        resetAudio();
        setProgress(0);
        const blobUrl = URL.createObjectURL(data);
        const fileReader = new FileReader();
        fileReader.onprogress = (event) => {
            setProgress(event.loaded / event.total || 0);
        };
        fileReader.onloadend = async () => {
            const audioCTX = new AudioContext({
                sampleRate: Constants.SAMPLING_RATE,
            });
            const arrayBuffer = fileReader.result as ArrayBuffer;
            const decoded = await audioCTX.decodeAudioData(arrayBuffer);
            setProgress(undefined);
            setAudioData({
                buffer: decoded,
                url: blobUrl,
                source: AudioSource.RECORDING,
                mimeType: data.type,
            });
        };
        fileReader.readAsArrayBuffer(data);
    };

    const downloadAudioFromUrl = async (
        requestAbortController: AbortController,
    ) => {
        if (audioDownloadUrl) {
            try {
                setAudioData(undefined);
                setProgress(0);

                const response = await fetch(audioDownloadUrl, {
                    signal: requestAbortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentLength = response.headers.get('content-length');
                const total = contentLength ? parseInt(contentLength, 10) : 0;

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error('Failed to get response reader');
                }

                const chunks: Uint8Array[] = [];
                let receivedLength = 0;

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) break;

                    chunks.push(value);
                    receivedLength += value.length;

                    if (total > 0) {
                        setProgress(receivedLength / total);
                    }
                }

                const arrayBuffer = new ArrayBuffer(receivedLength);
                const uint8Array = new Uint8Array(arrayBuffer);
                let position = 0;
                for (const chunk of chunks) {
                    uint8Array.set(chunk, position);
                    position += chunk.length;
                }

                const mimeType = response.headers.get('content-type') || 'audio/wav';
                setAudioFromDownload(arrayBuffer, mimeType);
            } catch (error) {
                console.log("Request failed or aborted", error);
            } finally {
                setProgress(undefined);
            }
        }
    };

    // When URL changes, download audio
    useEffect(() => {
        if (audioDownloadUrl) {
            const requestAbortController = new AbortController();
            downloadAudioFromUrl(requestAbortController);
            return () => {
                requestAbortController.abort();
            };
        }
    }, [audioDownloadUrl]);

    useEffect(() => {
    if (!audioData) return;
    if(!props.isAIModulePage) return;

    // Run full transcription silently
    const runBackgroundTranscription = async () => {
        try {
            await props.transcriber.start(audioData.buffer);
            props.isBackgroundTranscribing.current = false;
        } catch (err) {
            console.error("Background transcription failed", err);
        }
    };

    runBackgroundTranscription();

}, [audioData, props.isAIModulePage]);

    useEffect(() => {
        if (props.isAIModulePage) {
            props.transcriber.setMode("SEGMENTED")
        } else {
            props.transcriber.setMode("FULL")
        }
    }, [props.isAIModulePage]);

    // const sliceAudioBuffer = (
    //     audioBuffer: AudioBuffer,
    //     startSample: number,
    //     endSample: number
    // ) => {
    //     const frameCount = endSample - startSample;
    //     const sampleRate = audioBuffer.sampleRate;

    //     const audioCtx = new AudioContext({ sampleRate });

    //     const slicedBuffer = audioCtx.createBuffer(
    //         audioBuffer.numberOfChannels,
    //         frameCount,
    //         sampleRate
    //     );

    //     for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    //         const channelData = audioBuffer.getChannelData(channel);
    //         slicedBuffer.copyToChannel(
    //             channelData.slice(startSample, endSample),
    //             channel
    //         );
    //     }

    //     return slicedBuffer;
    // };

    //         const handleSegmentedTranscription = () => {
    //     if (!audioData) return;
    //     if (!props.isAIModulePage) return;
    //     const startTime = props.startTimeRef?.current || 0;
        
    //     const endTime = props.pauseTimeRef?.current || audioData.buffer.duration;
       
    //     if (endTime - startTime < 1.5) {
    //         console.log("Segment too short, skipping transcription");
    //         return
    //     }
    //     const sampleRate = audioData.buffer.sampleRate;
    //     const startSample = Math.floor(startTime * sampleRate);
    //     const endSample = Math.floor(endTime * sampleRate);
    //     if (endSample <= startSample) {
    //         console.log("Invalid segment, skipping transcription");
    //         return;
    //     }
    //     const segmentBuffer = sliceAudioBuffer(
    //         audioData.buffer,
    //         startSample,
    //         endSample
    //     );
    //     props.transcriber.start(segmentBuffer);
    //     lastProccessedTimeRef.current = endTime;
    // }


    // useEffect (()=>{
    //     if(props.isPaused === false){
    //         console.log("AudioManager detected isPaused false, early exiting");
    //         return;
    //     }
    // handleSegmentedTranscription();
    // }, [props.isPaused])



    return (
        <>
            {/* <div className='flex flex-col justify-center items-center shadow-md shadow-blue-500/20 ring-1 ring-blue-400/30 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800'>
                <div
                    className='flex flex-row space-x-2 py-2 w-full px-2'
                    style={{
                        pointerEvents: props.isTranscribing ? 'none' : 'auto',
                        opacity: props.isTranscribing ? 0.5 : 1,
                    }}
                    > */}
            {/* <UrlTile
                        icon={<AnchorIcon />}
                        text={"From URL"}
                        onUrlUpdate={(e) => {
                        props.transcriber.onInputChange();
                        setAudioDownloadUrl(e);
                        }}
                    />
                    <VerticalBar /> */}
            {props?.isAIModulePage === true ? (showFileInput &&  <FileTile
                icon={<FolderIcon />}
                text={"From file"}
                onFileUpdate={(decoded, blobUrl, mimeType) => {
                    props.transcriber.onInputChange();
                    setAudioData({
                        buffer: decoded,
                        url: blobUrl,
                        source: AudioSource.FILE,
                        mimeType: mimeType,
                    });
                    setShowFileInput(false);
                }}
                isDisabled={props.transcriber.isBusy || props.transcriber.isModelLoading}
            />):(<FileTile
                icon={<FolderIcon />}
                text={"From file"}
                onFileUpdate={(decoded, blobUrl, mimeType) => {
                    props.transcriber.onInputChange();
                    setAudioData({
                        buffer: decoded,
                        url: blobUrl,
                        source: AudioSource.FILE,
                        mimeType: mimeType,
                    });
                }}
                isDisabled={props.transcriber.isBusy || props.transcriber.isModelLoading}
            />)}
            {/* {navigator.mediaDevices && (
                        <>
                        <VerticalBar />
                        <RecordTile
                            icon={<MicrophoneIcon />}
                            text={"Record"}
                            setAudioData={(e) => {
                            props.transcriber.onInputChange();
                            setAudioFromRecording(e);
                            }}
                        />
                        </>
                    )} */}
            {/* </div> */}
            {/* </div> */}
            {audioData && (
                <>
                    <AudioPlayer
                        audioUrl={audioData.url}
                        mimeType={audioData.mimeType}
                        isAIModulePage={props.isAIModulePage}
                        // handleSegmentedTranscription={handleSegmentedTranscription}
                        isProcessing={props.isProcessing}
                    />

                    {/* {!props.isTranscriptionCompleted &&  */}
                    <div className='relative w-full flex justify-center items-center'>
                        {props.isAIModulePage ? (null) : (<TranscribeButton
                            onClick={() => {
                                props.transcriber.start(audioData.buffer);
                            }}
                            isModelLoading={props.transcriber.isModelLoading}
                            isTranscribing={props.transcriber.isBusy}
                            // isDisableButton = {props.isDisableButton}
                            isCreatingAiJob={props.isCreatingAiJob}
                            isTranscribed={!!props.transcriber.output?.text}
                        />)}
                        {!props.transcriber.isModelLoading && !props.transcriber.isBusy && props.transcriber.output?.text && !props.isAIModulePage &&
                            <Button className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary 
                                text-primary-foreground font-semibold px-10 py-5 rounded-xl shadow-lg 
                                hover:shadow-xl transition-all duration-300 transform hover:scale-105 
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none 
                                flex items-center justify-center gap-2 ms-12"
                                onClick={() => {
                                    if (props.isEditingTranscription) {
                                        toast.error("You have unsaved changes in the transcription. Please save before continuing.");
                                        return;
                                    }
                                    if (props.createAiJob) {
                                        props.createAiJob()
                                    }
                                }}
                                disabled={props.isCreatingAiJob}>
                                {props.isCreatingAiJob ? (
                                    <>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        {props.jobError ? "Try again..." : "Next"}
                                    </>
                                ) : (
                                    props.jobError ? "Try again!" : "Next"
                                )}
                                <ArrowRight className="w-5 h-5" />
                            </Button>

                        }
                    </div>
                </>
            )}
        </>
    );
}

function SettingsTile(props: {
    icon: JSX.Element;
    className?: string;
    transcriber: Transcriber;
}) {
    const [showModal, setShowModal] = useState(false);

    const onClick = () => {
        setShowModal(true);
    };

    const onClose = () => {
        setShowModal(false);
    };

    const onSubmit = (url: string) => {
        onClose();
    };

    return (
        <div className={props.className}>
            <Tile icon={props.icon} onClick={onClick} />
            <SettingsModal
                show={showModal}
                onSubmit={onSubmit}
                onClose={onClose}
                transcriber={props.transcriber}
            />
        </div>
    );
}

function SettingsModal(props: {
    show: boolean;
    onSubmit: (url: string) => void;
    onClose: () => void;
    transcriber: Transcriber;
}) {
    const names = Object.values(LANGUAGES).map(titleCase);

    const models = {
        // Original checkpoints
        'Xenova/whisper-tiny': [41, 152],
        'Xenova/whisper-base': [77, 291],
        'Xenova/whisper-small': [249],
        'Xenova/whisper-medium': [776],

        // Distil Whisper (English-only)
        'distil-whisper/distil-medium.en': [402],
        'distil-whisper/distil-large-v2': [767],
    };
    return (
        <Modal
            show={props.show}
            title={"Settings"}
            content={
                <>
                    <label>Select the model to use.</label>
                    <select
                        className='mt-1 mb-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                        defaultValue={props.transcriber.model}
                        onChange={(e) => {
                            props.transcriber.setModel(e.target.value);
                        }}
                    >
                        {Object.keys(models)
                            .filter(
                                (key) =>
                                    props.transcriber.quantized ||
                                    // @ts-ignore
                                    models[key].length == 2,
                            )
                            .filter(
                                (key) => (
                                    !props.transcriber.multilingual || !key.startsWith('distil-whisper/')
                                )
                            )
                            .map((key) => (
                                <option key={key} value={key}>{`${key}${(props.transcriber.multilingual || key.startsWith('distil-whisper/')) ? "" : ".en"
                                    } (${
                                    // @ts-ignore
                                    models[key][
                                    props.transcriber.quantized ? 0 : 1
                                    ]
                                    }MB)`}</option>
                            ))}
                    </select>
                    <div className='flex justify-between items-center mb-3 px-1'>
                        <div className='flex'>
                            <input
                                id='multilingual'
                                type='checkbox'
                                checked={props.transcriber.multilingual}
                                onChange={(e) => {
                                    props.transcriber.setMultilingual(
                                        e.target.checked,
                                    );
                                }}
                            ></input>
                            <label htmlFor={"multilingual"} className='ms-1'>
                                Multilingual
                            </label>
                        </div>
                        <div className='flex'>
                            <input
                                id='quantize'
                                type='checkbox'
                                checked={props.transcriber.quantized}
                                onChange={(e) => {
                                    props.transcriber.setQuantized(
                                        e.target.checked,
                                    );
                                }}
                            ></input>
                            <label htmlFor={"quantize"} className='ms-1'>
                                Quantized
                            </label>
                        </div>
                    </div>
                    {props.transcriber.multilingual && (
                        <>
                            <label>Select the source language.</label>
                            <select
                                className='mt-1 mb-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                                defaultValue={props.transcriber.language}
                                onChange={(e) => {
                                    props.transcriber.setLanguage(
                                        e.target.value,
                                    );
                                }}
                            >
                                {Object.keys(LANGUAGES).map((key, i) => (
                                    <option key={key} value={key}>
                                        {names[i]}
                                    </option>
                                ))}
                            </select>
                            <label>Select the task to perform.</label>
                            <select
                                className='mt-1 mb-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                                defaultValue={props.transcriber.subtask}
                                onChange={(e) => {
                                    props.transcriber.setSubtask(
                                        e.target.value,
                                    );
                                }}
                            >
                                <option value={"transcribe"}>Transcribe</option>
                                <option value={"translate"}>
                                    Translate (to English)
                                </option>
                            </select>
                        </>
                    )}
                </>
            }
            onClose={props.onClose}
            onSubmit={() => { }}
        />
    );
}

function VerticalBar() {
    return <div className='w-[1px] bg-slate-200'></div>;
}

function UrlTile(props: {
    icon: JSX.Element;
    text: string;
    onUrlUpdate: (url: string) => void;
}) {
    const [showModal, setShowModal] = useState(false);

    const onClick = () => {
        setShowModal(true);
    };

    const onClose = () => {
        setShowModal(false);
    };

    const onSubmit = (url: string) => {
        props.onUrlUpdate(url);
        onClose();
    };

    return (
        <>
            <Tile icon={props.icon} text={props.text} onClick={onClick} />
            <UrlModal show={showModal} onSubmit={onSubmit} onClose={onClose} />
        </>
    );
}

function UrlModal(props: {
    show: boolean;
    onSubmit: (url: string) => void;
    onClose: () => void;
}) {
    const [url, setUrl] = useState(Constants.DEFAULT_AUDIO_URL);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUrl(event.target.value);
    };

    const onSubmit = () => {
        props.onSubmit(url);
    };

    return (
        <Modal
            show={props.show}
            title={"From URL"}
            content={
                <>
                    {"Enter the URL of the audio file you want to load."}
                    <UrlInput onChange={onChange} value={url} />
                </>
            }
            onClose={props.onClose}
            submitText={"Load"}
            onSubmit={onSubmit}
        />
    );
}

// function FileTile(props: {
//     icon: JSX.Element;
//     text: string;
//     onFileUpdate: (
//         decoded: AudioBuffer,
//         blobUrl: string,
//         mimeType: string,
//     ) => void;
// }) {
//     // const audioPlayer = useRef<HTMLAudioElement>(null);

//     // Create hidden input element
//     const elem = document.createElement("input");
//     elem.type = "file";
//     elem.oninput = (event) => {
//         // Make sure we have files to use
//         const files = (event.target as HTMLInputElement).files;
//         if (!files) return;

//         const file = files[0];
//         // Create a blob that we can use as an src for our audio element
//         const urlObj = URL.createObjectURL(file);
//         const mimeType = file.type;

//         const reader = new FileReader();
//         reader.addEventListener("load", async (e) => {
//             const arrayBuffer = e.target?.result as ArrayBuffer; // Get the ArrayBuffer
//             if (!arrayBuffer) return;

//             const audioCTX = new AudioContext({
//                 sampleRate: Constants.SAMPLING_RATE,
//             });

//             const decoded = await audioCTX.decodeAudioData(arrayBuffer);

//             props.onFileUpdate(decoded, urlObj, mimeType);
//         });
//         reader.readAsArrayBuffer(files[0]);

//         // Reset files
//         elem.value = "";
//     };

//     return (
//         <>
//             <Tile
//                 icon={props.icon}
//                 text={props.text}
//                 onClick={() => elem.click()}
//             />
//         </>
//     );
// }

function RecordTile(props: {
    icon: JSX.Element;
    text: string;
    setAudioData: (data: Blob) => void;
}) {
    const [showModal, setShowModal] = useState(false);

    const onClick = () => {
        setShowModal(true);
    };

    const onClose = () => {
        setShowModal(false);
    };

    const onSubmit = (data: Blob | undefined) => {
        if (data) {
            props.setAudioData(data);
            onClose();
        }
    };

    return (
        <>
            <Tile icon={props.icon} text={props.text} onClick={onClick} />
            <RecordModal
                show={showModal}
                onSubmit={onSubmit}
                onClose={onClose}
            />
        </>
    );
}

function RecordModal(props: {
    show: boolean;
    onSubmit: (data: Blob | undefined) => void;
    onClose: () => void;
}) {
    const [audioBlob, setAudioBlob] = useState<Blob>();

    const onRecordingComplete = (blob: Blob) => {
        setAudioBlob(blob);
    };

    const onSubmit = () => {
        props.onSubmit(audioBlob);
        setAudioBlob(undefined);
    };

    const onClose = () => {
        props.onClose();
        setAudioBlob(undefined);
    };

    return (
        <Modal
            show={props.show}
            title={"From Recording"}
            content={
                <>
                    {"Record audio using your microphone"}
                    <AudioRecorder onRecordingComplete={onRecordingComplete} />
                </>
            }
            onClose={onClose}
            submitText={"Load"}
            submitEnabled={audioBlob !== undefined}
            onSubmit={onSubmit}
        />
    );
}

function Tile(props: {
    icon: JSX.Element;
    text?: string;
    onClick?: () => void;
}) {
    return (
        <button
            onClick={props.onClick}
            className='flex items-center justify-center rounded-lg p-2 bg-blue text-slate-500 hover:text-blue-500  hover:scale-[1.02] hover:shadow-blue-500/30 transition-all duration-200'
        >
            <div className='w-7 h-7'>{props.icon}</div>
            {props.text && (
                <div className='ml-2 break-text text-center text-md w-30'>
                    {props.text}
                </div>
            )}
        </button>
    );
}

function AnchorIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
            />
        </svg>
    )
}

function FolderIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
            />
        </svg>
    )
}

// function SettingsIcon() {
//     return (
//         <svg
//             xmlns='http://www.w3.org/2000/svg'
//             fill='none'
//             viewBox='0 0 24 24'
//             strokeWidth='1.25'
//             stroke='currentColor'
//         >
//             <path
//                 strokeLinecap='round'
//                 strokeLinejoin='round'
//                 d='M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z'
//             />
//             <path
//                 strokeLinecap='round'
//                 strokeLinejoin='round'
//                 d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
//             />
//         </svg>
//     );
// }

function MicrophoneIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
        </svg>
    )
}
